<template>
  <div>
    <h1 class="title">Sent Referrals / Requests</h1>
    <div
      class="d-flex justify-content-end my-4"
      v-if="!requestReferralsLoading && requestReferralsData.length > 0"
    >
      <button
        @click="deleteRequests()"
        class="btn btn-primary btn-danger mt-sm-0 mt-3 mr-2"
      >
        <i class="fas fa-trash"></i> Delete
        {{ selectedRequests.length ? "Selected" : "All" }}
      </button>
    </div>
    <alert
      v-if="!requestReferralsLoading && requestReferralsData.length == 0"
      class="white-bg shadow-card"
      >No Referral Requests</alert
    >
    <loading
      v-if="requestReferralsLoading && requestReferralsData.length == 0"
      class="white-bg shadow-card"
    />
    <TablePagination
      v-if="requestReferralsData.length > 0"
      @onPageChange="onPageChangeHelper"
      :count="total"
      :rowsPerPage="limit"
      :currentPage="currentPage"
      showTopPagination
      showFirstButton
      showLastButton
    >
      <loading
        v-if="requestReferralsLoading"
        class="white-bg my-5 mx-3 alert alert-light"
      />
      <div class="card requests-card" v-if="!requestReferralsLoading">
        <div class="table-responsive rounded" style="overflow-x: auto">
          <table class="table table-striped mb-0">
            <thead>
              <tr>
                <th class="vendor-cell">Vendor</th>
                <th>Service Type</th>
                <th style="min-width: 118px">Rating</th>
                <th>Status</th>
                <th class="text-center created-cell">Created</th>
                <th class="text-center due-cell">Due Date</th>
                <th>Details</th>
                <th>Actions</th>
                <th>
                  <input
                    class="cursor-pointer"
                    type="checkbox"
                    v-model="selectAll"
                    @change="selectDeselectAll"
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="order in requestReferralsData"
                :key="order.id"
                class="mobile-tablet-shadow-card rounded"
              >
                <td>
                  <b class="mobile-tablet-only mr-2">Vendor:</b>
                  {{ (order.vendor || {}).name }}
                  <br />
                </td>
                <td>
                  <b class="mobile-tablet-only mr-2">Service Type:</b>
                  <badge
                    v-if="order.sub_service"
                    class="badge-light service-type"
                    >{{ order.service }} / {{ order.sub_service }}</badge
                  >
                  <badge v-else class="badge-light">{{ order.service }}</badge>
                  <div v-if="order.attachments.length > 0" class="py-2">
                    <h3 class="my-2">Attachment(s)</h3>
                    <p
                      class="text-muted small p-0 m-0"
                      v-for="attachment in order.attachments"
                      :key="attachment.id"
                    >
                      <span v-if="!attachment.isExpired">
                        <a :href="`${attachment.url}`" target="_blank">
                          <icon type="file" />
                          {{ attachment.name }}
                        </a>
                      </span>
                      <span v-else>
                        <badge color="danger">Expired</badge>
                        {{ attachment.name }}
                      </span>
                    </p>
                  </div>
                </td>
                <td>
                  <b
                    class="mobile-tablet-only mr-2"
                    v-if="!order.deleted_form_vendor"
                    >Rating:</b
                  >
                  <rating
                    :order="order"
                    v-if="!order.deleted_form_vendor"
                    class="rating-value"
                  />
                </td>
                <td>
                  <b class="mobile-tablet-only mr-2">Status:</b>
                  <badge
                    style="white-space: normal; line-height: 1.5"
                    :class="
                      labelColor(
                        order.status,
                        parseInt(order.deleted_form_vendor)
                      )
                    "
                  >
                    {{
                      parseInt(order.deleted_form_vendor)
                        ? "DELETED BY RECEIVER"
                        : order.status
                    }}</badge
                  >
                </td>
                <td class="text-center">
                  <b class="mobile-tablet-only mr-2" v-if="order.created_at"
                    >Created:</b
                  >{{ order.created_at | fullDate }}
                </td>
                <td class="text-center">
                  <b class="mobile-tablet-only mr-2" v-if="order.due_at"
                    >Due Date:</b
                  >{{ order.due_at | fullDate }}
                </td>
                <td>
                  <b class="mobile-tablet-only mr-2" v-if="order.details"
                    >Details:</b
                  >
                  <button
                    class="btn btn-sm order-details-btn"
                    v-if="order.details && order.details.trim().length > 20"
                    @click="viewOrderDetails(order.details)"
                  >
                    View
                  </button>
                  <span
                    v-if="order.details && order.details.trim().length <= 20"
                    >{{ order.details }}</span
                  >
                </td>
                <td class="delete-td">
                  <button
                    @click="deleteRequest(order.id)"
                    class="btn text-danger"
                  >
                    <i class="fas fa-trash" title="delete"></i>
                    <span class="mobile-tablet-only pl-2">Delete</span>
                  </button>
                </td>
                <td>
                  <input
                    class="cursor-pointer"
                    type="checkbox"
                    :id="`check_request_${order.id}_id`"
                    v-model="order.selected"
                    @change="checkSelectedRequests"
                  />
                  <label
                    :for="`check_request_${order.id}_id`"
                    class="mobile-tablet-only ml-2 cursor-pointer"
                    >Select to delete</label
                  >
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </TablePagination>
    <ShowContentModal title="Order Details" :content="selectedDetails" />
  </div>
</template>
<script>
import { mapActions, mapState } from "vuex";
import Rating from "@/components/Rating";
import Swal from "sweetalert2";
import TablePagination from "../../components/TablePagination.vue";
import ShowContentModal from "./ShowContentModal.vue";

export default {
  name: "Requests",
  components: {
    Rating,
    TablePagination,
    ShowContentModal,
  },
  data() {
    return {
      limit: 50,
      selectAll: false,
      selectedRequests: [],
      selectedDetails: "",
    };
  },
  computed: {
    ...mapState(["orders"]),
    ...mapState({
      requestReferralsData: (state) => state.orders.requests.pagination.data,
      total: (state) => state.orders.requests.pagination.total,
      currentPage: (state) => state.orders.requests.pagination.current_page,
      requestReferralsLoading: (state) => state.orders.isLoading.requests,
    }),
  },
  mounted() {
    this.getRequestReferralsHelper(this.$route.query.pageNumber || 1);
  },
  methods: {
    ...mapActions({
      getRequestReferrals: "orders/allRequests",
    }),
    labelColor: function (status, deleted) {
      if (deleted) {
        return "badge-danger";
      } else if (status == "RECEIVED") {
        return "badge-secondary badge-soft-secondary";
      } else if (status == "FULFILLED") {
        return "badge-warning badge-soft-warning";
      } else if (status == "SHIPPED") {
        return "badge-info badge-soft-info";
      } else if (status == "COMPLETED") {
        return "badge-success badge-soft-success";
      } else if (status == "Your appointment has been scheduled") {
        return "text-uppercase badge-primary badge-primary-force";
      } else if (
        status == "Our team will be contacting you soon to discuss request"
      ) {
        return "text-uppercase badge-warning";
      } else if (
        status == "Vendor does not offer requested service(s) and/or product(s)"
      ) {
        return "text-uppercase badge-danger";
      } else if (status == "Vendor is requesting you re-send information") {
        return "text-uppercase badge-dark";
      } else if (status !== "PENDING") {
        return "text-uppercase badge-info";
      }
    },
    selectDeselectAll: function () {
      this.requestReferralsData.forEach(
        (request) => (request.selected = this.selectAll)
      );
      this.checkSelectedRequests();
    },
    checkSelectedRequests: function () {
      const requests = [];
      this.requestReferralsData.forEach((request) => {
        if (request.selected) {
          requests.push(request.id);
        }
      });
      if (requests.length == this.requestReferralsData.length) {
        this.selectAll = true;
      } else {
        this.selectAll = false;
      }
      this.selectedRequests = requests;
    },
    deleteRequests: function () {
      const vm = this;
      const swalWithBootstrapButtons = Swal.mixin({
        customClass: {
          confirmButton: "btn btn-success ml-4",
          cancelButton: "btn btn-danger",
        },
        buttonsStyling: false,
      });

      swalWithBootstrapButtons
        .fire({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          type: "warning",
          showCancelButton: true,
          confirmButtonText: `Yes, delete ${
            vm.selectedRequests.length ? "selected" : "all"
          }`,
          cancelButtonText: "No, cancel",
          reverseButtons: true,
        })
        .then((result) => {
          if (result.value) {
            let actionName = "orders/deleteAllRequests";
            if (vm.selectedRequests.length) {
              actionName = "orders/deleteSelectedRequests";
            }
            vm.$store
              .dispatch(actionName, vm.selectedRequests)
              .then((deleted) => {
                if (deleted) {
                  this.getRequestReferralsHelper(this.currentPage);
                  swalWithBootstrapButtons.fire(
                    "Deleted!",
                    "Your sent referrals have been deleted.",
                    "success"
                  );
                } else {
                  swalWithBootstrapButtons.fire(
                    "Error!",
                    "Something went wrong. Please refresh the page and try again",
                    "error"
                  );
                }
              });
          }
        });
    },
    deleteRequest: function (id) {
      const vm = this;
      const swalWithBootstrapButtons = Swal.mixin({
        customClass: {
          confirmButton: "btn btn-success ml-4",
          cancelButton: "btn btn-danger",
        },
        buttonsStyling: false,
      });

      swalWithBootstrapButtons
        .fire({
          title: "Are you sure?",
          text: "You won't be able to revert this!",
          type: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes, delete",
          cancelButtonText: "No, cancel",
          reverseButtons: true,
        })
        .then((result) => {
          if (result.value) {
            vm.$store.dispatch("orders/deleteRequest", id).then((deleted) => {
              if (deleted) {
                this.getRequestReferralsHelper(this.currentPage);
                swalWithBootstrapButtons.fire(
                  "Deleted!",
                  "Sent referral has been deleted.",
                  "success"
                );
              } else {
                swalWithBootstrapButtons.fire(
                  "Error!",
                  "Something went wrong. Please refresh the page and try again",
                  "error"
                );
              }
            });
          }
        });
    },
    getRequestReferralsHelper: function (page) {
      const payload = {
        limit: this.limit,
        page,
      };
      this.getRequestReferrals(payload).then((res) => {
        if (res) {
          if (page != this.$route.query.pageNumber) {
            this.$router.replace({
              path: this.$route.path,
              query: {
                pageNumber: page,
              },
            });
          }
        }
      });
    },
    onPageChangeHelper: function (page) {
      this.getRequestReferralsHelper(page);
    },
    viewOrderDetails: function (details) {
      this.selectedDetails = details;
      this.$store.dispatch("showModal", "show-content-modal");
    },
  },
};
</script>

<style scoped lang="scss">
.due-cell,
.created-cell {
  min-width: 152px;
}

.vendor-cell {
  min-width: 200px;
}
.text-muted {
  color: #6286ba !important;
}
.order-details-btn {
  background-color: #0e43ad;
  color: #fff;
}
@media (max-width: 1024px) {
  .requests.section {
    .requests-card {
      box-shadow: none !important;
      border: 0 !important;
      background-color: transparent !important;
    }
    table {
      display: flex;
    }
    thead {
      display: none;
    }
    tbody {
      width: 100%;
    }
    tr {
      display: flex;
      flex-direction: column;
      margin-bottom: 24px;
      padding: 8px;
      td {
        padding: 0 4px;
        text-align: left !important;
        border: 0 !important;
        b {
          display: inline-block;
          padding: 4px 0;
        }
        .text-muted {
          color: #152e4d !important;
        }
        .rating-value {
          display: inline-block;
        }

        .service-type {
          max-width: 100%;
          text-overflow: ellipsis;
          overflow: hidden;
        }

        &.delete-td {
          text-align: center !important;
          margin-bottom: 8px;
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid #ccc !important;
          .btn {
            padding: 4px 8px;
            color: #fff !important;
            background-color: #e63757;
            border-color: #e63757;
          }
        }
      }
    }
  }
}
</style>
